import React from "react";
import { Badge, Box, Button, Grid, GridItem, HStack, Text, Tooltip, VStack } from "@chakra-ui/react";
import type { TesterConfig } from "./types";

interface Props {
  config: TesterConfig;
  isLeanSupplier: boolean;
  isCustomized: boolean;
  disabled: boolean;
  onEdit: () => void;
  onReset: () => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <GridItem>
    <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.06em" color="gray.500" fontWeight="600">
      {label}
    </Text>
    <Box fontSize="sm" color="gray.800" mt={0.5}>
      {children}
    </Box>
  </GridItem>
);

const ConfigSummaryCard: React.FC<Props> = ({ config, isLeanSupplier, isCustomized, disabled, onEdit, onReset }) => {
  const address = config.shipmentAddress as any;
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} opacity={disabled ? 0.7 : 1} transition="opacity 0.2s">
      <HStack justify="space-between" mb={3}>
        <HStack>
          <Text fontWeight="700" fontSize="sm">
            Test configuration
          </Text>
          <Badge colorScheme={isCustomized ? "blue" : "gray"} variant="subtle" fontSize="10px">
            {isCustomized ? "saved preferences" : "defaults from orca_test_order_prefill_config"}
          </Badge>
        </HStack>
        <HStack>
          <Tooltip label="Restore defaults from orca_test_order_prefill_config">
            <Button size="xs" variant="ghost" onClick={onReset} isDisabled={disabled || !isCustomized}>
              Reset preferences
            </Button>
          </Tooltip>
          <Button size="xs" colorScheme="blue" variant="outline" onClick={onEdit} isDisabled={disabled}>
            Edit configuration
          </Button>
        </HStack>
      </HStack>
      <Grid templateColumns="repeat(4, 1fr)" gap={4}>
        <Field label="Customer">
          <HStack spacing={1} align="baseline">
            <Text fontWeight="600" noOfLines={1}>
              {config.customerName || config.customerId || "—"}
            </Text>
            {config.customerId && config.isTestCustomer !== false && (
              <Badge colorScheme="orange" fontSize="9px">TEST</Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {config.customerCode} {config.customerMobile ? `· ${config.customerMobile}` : ""}
          </Text>
        </Field>
        <Field label="Supplier">
          <HStack spacing={1} align="baseline">
            <Text fontWeight="600" noOfLines={1}>
              {config.supplierName || config.supplierId || "—"}
            </Text>
            {config.supplierId && <Badge colorScheme="orange" fontSize="9px">TEST</Badge>}
          </HStack>
          <Text fontSize="xs" color="gray.500">
            {config.supplierId ? (isLeanSupplier ? "lean review flow" : "autonomous") : ""}
          </Text>
        </Field>
        <Field label="Address">
          <Text noOfLines={2} fontSize="xs">
            {address ? [address.addressLine1, address.city, address.state, address.pinCode].filter(Boolean).join(", ") : "—"}
          </Text>
        </Field>
        <Field label="Parts">
          <VStack align="stretch" spacing={0}>
            {config.parts.slice(0, 3).map((p) => (
              <Text key={p.partId} fontSize="xs" noOfLines={1}>
                {p.partNumber || p.partId} × {p.quantity}
              </Text>
            ))}
            {config.parts.length > 3 && (
              <Text fontSize="xs" color="gray.500">
                +{config.parts.length - 3} more
              </Text>
            )}
            {!config.parts.length && <Text fontSize="xs">—</Text>}
          </VStack>
        </Field>
        <Field label="Transaction">
          <Text fontSize="xs">
            {config.transactionType} · {config.payableBy} → {config.payableTo}
          </Text>
        </Field>
        <Field label="Price estimate">
          <Text fontSize="xs">
            {config.requirePriceEstimate ? "Required (draft → final estimate)" : "Not required (existing flow)"}
          </Text>
        </Field>
        <Field label="Final estimate states">
          <Text fontSize="xs">
            {config.finalEstimateStates ? "On (requested → created → shared)" : "Off (existing flow)"}
          </Text>
        </Field>
        <Field label="Delivery option">
          <Text fontSize="xs">
            {config.preferredDelivery === "INSTANT"
              ? "Instant Delivery"
              : config.preferredDelivery === "SELF_PICKUP"
              ? "Self Pickup"
              : "Standard Delivery"}
          </Text>
        </Field>
        <Field label="Dispatch">
          <Text fontSize="xs">
            {config.dispatchMode}
            {config.dispatchMode === "SELF_DISPATCH" ? ` · ${config.rider.name}` : ""}
          </Text>
        </Field>
        <Field label="Media / remarks">
          <Text fontSize="xs" noOfLines={2}>
            {config.orderMediaIds.length} file(s) · {config.remarks || "no remarks"}
          </Text>
        </Field>
        <Field label="Polling">
          <Text fontSize="xs">
            {config.pollMaxAttempts} attempts · 2s/3s/5s · invoice: {config.invoiceEventPolicy}
          </Text>
        </Field>
      </Grid>
    </Box>
  );
};

export default ConfigSummaryCard;
